// Clients API — типизированные обёртки над backend-эндпоинтами.
// Контракт сверен с apps/api clients.controller + list-clients.query (Sprint 11).

import type { MessengerPlatform } from '@app/domain';

import { api } from '@/lib/api';
import { setBranchParams } from '@/lib/branch-params';

import type { TaskAutoKind } from './tasks.api';

export type ClientStatus =
  | 'lead'
  | 'newcomer'
  | 'member'
  | 'ending'
  | 'lapsed'
  | 'dormant'
  | 'refused'
  | 'representative'
  | 'archived'
  | 'deletion_pending';

export type ConsentSource = 'web_form' | 'paper_scan' | 'phone_recording' | 'admin_entry';

/** Действующий абонемент клиента (элемент списка для одноимённого столбца). */
export interface ClientActiveSubscription {
  id: string;
  /** Название шаблона абонемента (что показываем в строке ячейки). */
  templateName: string;
  /** Тип шаблона абонемента (служебное, на будущее). */
  templateType: string;
  /** Дата активации абонемента 'YYYY-MM-DD' (backend кастует к text date). */
  activatedAt: string;
  /** Дата окончания действия 'YYYY-MM-DD' либо null (бессрочный / не задано). */
  expiresAt: string | null;
  /** Осталось занятий (лимитные абонементы) либо null (безлимит). */
  sessionsRemaining: number | null;
  /** Всего занятий в абонементе (лимитные) либо null (безлимит). */
  sessionsTotal: number | null;
  /** «Фамилия Имя» продавца (с продажи). null у импорта — продажи в CRM нет. */
  soldByName: string | null;
}

/** Открытая задача клиента (для колонки «Задача» в списке). */
export interface ClientOpenTask {
  id: string;
  title: string;
  due_date: string | null;
  /** id ответственного сотрудника либо null (не назначен). */
  assigned_to_employee_id: string | null;
  /** «Фамилия Имя» ответственного либо null (не назначен). */
  assignee_name: string | null;
  /** null — поставлена человеком. Часть авто-задач руками не закрывается (план 98). */
  auto_kind: TaskAutoKind | null;
}

export interface ClientListItem {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  phone: string | null;
  email: string | null;
  status: ClientStatus;
  /** Карточка влита в другую при объединении (docs/55); null — самостоятельная. */
  mergedIntoClientId: string | null;
  /** Имя победителя объединения — для плашки «Объединён с …». */
  mergedIntoName: string | null;
  primaryBranchId: string | null;
  /**
   * Короткое имя основного филиала и город карточки — подпись «где живёт» в
   * окнах объединения дублей (правка владельца 29.09). Сервер отдаёт всегда;
   * необязательны для тестовых заготовок, собранных до появления полей.
   */
  primaryBranchName?: string | null;
  cityName?: string | null;
  // Поля профиля карточки — для настраиваемых столбцов списка.
  birthDate: string | null;
  gender: 'm' | 'f' | null;
  sourceName: string | null;
  /** Д5 (батч №18): есть привязанный диалог мессенджера — для иконки «написать в чат». */
  hasMessenger: boolean;
  activeCardNumber: string | null;
  /** Направления (стили), к которым относится клиент — по посещениям. */
  directionNames: string[];
  /** Группы, к которым относится клиент — по посещениям. */
  groupNames: string[];
  /** ISO-datetime последнего посещения (status='attended') либо null. */
  lastVisitAt: string | null;
  /** Все действующие абонементы клиента (пустой массив — нет действующих). */
  activeSubscriptions: ClientActiveSubscription[];
  /** Ближайшая открытая задача клиента (null — открытых задач нет). */
  open_task: ClientOpenTask | null;
  /** Сколько всего открытых задач у клиента (для индикатора «+N»). */
  open_task_count: number;
  /** Представители (может быть несколько — мать/отец); [] — нет. */
  representatives: Array<{ id: string; name: string; phone: string | null }>;
  /** Баланс депозита в копейках (0 — кошелёк не создан). */
  depositKopecks: number;
  /** Долг в копейках: продажи «в долг» + входящий долг из старой CRM. */
  debtKopecks: number;
}

export interface ListClientsResult {
  items: ClientListItem[];
  nextCursor: string | null;
}

export interface ListClientsParams {
  q?: string;
  status?: ClientStatus;
  /** Филиалы шапки (план 106, мультивыбор); пусто — все. */
  branchIds?: string[];
  /** Д6 (батч №18): только клиенты без привязки к филиалу. */
  noBranch?: boolean;
  directionId?: string;
  groupId?: string;
  /** План 68 П1: true — только с документами, false — только без них. */
  hasDocuments?: boolean;
  /** План 70: скрыть объединённые (архивные) карточки. */
  excludeMerged?: boolean;
  excludeArchived?: boolean;
  cursor?: string;
  limit?: number;
}

export type ClientType = 'client' | 'representative' | 'client_representative';

interface CreateClientBodyBase {
  first_name: string;
  last_name: string;
  middle_name?: string;
  phone: string;
  email?: string;
  birth_date?: string;
  gender?: 'm' | 'f';
  client_type?: ClientType;
  source_id?: string;
  consent_text_version_id: string;
  consent_source: ConsentSource;
  /**
   * Представитель ребёнка. Уезжает ВМЕСТЕ с карточкой: сервер требует взрослого
   * у клиента младше 18 и заводит связь в той же транзакции. Отдельным запросом
   * её больше не создаём — упавший второй вызов оставлял ребёнка без взрослого.
   */
  representative_client_id?: string;
}

/**
 * Где будет заниматься клиент — ровно одно из двух (план 157): филиал либо
 * только город («филиал уточнить позже» у менеджера города). С городом сервер
 * ставит задачу «Уточнить локацию».
 */
export type CreateClientBody = CreateClientBodyBase &
  ({ primary_branch_id: string; city_id?: never } | { city_id: string; primary_branch_id?: never });

export interface CreateClientResult {
  clientId: string;
  consentRecordId: string;
}

export interface BranchListItem {
  id: string;
  name: string;
  /** Короткое имя для тесных мест интерфейса (см. lib/branch-label). */
  shortName?: string | null;
  cityId: string;
  address: string | null;
}

export interface ActiveConsentVersion {
  id: string;
  versionNumber: number;
  validFrom: string;
}

function buildQuery(params: ListClientsParams): string {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.status) sp.set('status', params.status);
  setBranchParams(sp, params.branchIds);
  if (params.noBranch) sp.set('no_branch', '1');
  if (params.directionId) sp.set('direction_id', params.directionId);
  if (params.groupId) sp.set('group_id', params.groupId);
  // Строкой, а не '1'/'0': сервер ждёт enum 'true' | 'false' (см. DTO — там
  // разобрано, почему не coerce.boolean).
  if (params.hasDocuments !== undefined) {
    sp.set('has_documents', params.hasDocuments ? 'true' : 'false');
  }
  if (params.excludeMerged) sp.set('exclude_merged', 'true');
  // Ставит ТОЛЬКО список раздела «Контакты». Окна поиска клиента (запись,
  // продажа, привязка чата, родственники, задачи) ходят сюда же и обязаны
  // находить архивных — решение владельца 11.08.
  if (params.excludeArchived) sp.set('exclude_archived', 'true');
  if (params.cursor) sp.set('cursor', params.cursor);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function listClients(params: ListClientsParams = {}): Promise<ListClientsResult> {
  return api.get<ListClientsResult>(`/clients${buildQuery(params)}`);
}

/** Счётчики клиентов для дашбордов над списком (Всего / Активных / Заблокированных). */
export interface ClientStats {
  total: number;
  active: number;
  blocked: number;
  /** Счётчики по сегментам (clients.status → count) для дашбордов-фильтров. */
  by_status: Record<string, number>;
}

export function getClientStats(branchIds?: string[], noBranch?: boolean): Promise<ClientStats> {
  const sp = new URLSearchParams();
  setBranchParams(sp, branchIds);
  if (noBranch) sp.set('no_branch', '1');
  const qs = sp.toString();
  return api.get<ClientStats>(`/clients/stats${qs ? `?${qs}` : ''}`);
}

export interface ClientActiveCard {
  id: string;
  cardNumber: string;
  cardType: 'physical' | 'virtual';
  issuedAt: string;
}

export interface ClientRelation {
  relationId: string;
  otherClientId: string;
  relationType: string;
}

/** Номер карточки: вид и, для бывшего, дата, когда его сменили. */
export interface ClientPhone {
  id: string;
  phone: string;
  kind: 'primary' | 'extra' | 'former';
  isVerified: boolean;
  replacedAt: string | null;
}

export interface GetClientResult {
  id: string;
  /** Короткий номер карточки (план 116) — из него собирается ссылка /clients/1234. */
  no: string | null;
  /** Фактических посещений (attended, включая импорт) — для «Занятий посещено». */
  visitedCount: number;
  /** Карточка влита в другую при объединении (docs/55); null — самостоятельная. */
  mergedIntoClientId: string | null;
  /** Имя победителя — для баннера «Объединена с …». */
  mergedIntoName: string | null;
  firstName: string;
  lastName: string;
  middleName: string | null;
  /** Проксирующий URL фото клиента (П11). null — показываем инициалы. */
  photoUrl: string | null;
  phone: string | null;
  /** Доп. телефон для связи; клиент правит его сам в мини-аппе. */
  phoneSecondary: string | null;
  /**
   * Все номера карточки (план 71): основной, дополнительные и бывшие. Бывший
   * номер продолжает находить карточку и держит привязку мессенджеров, поэтому
   * карточка его показывает — иначе непонятно, почему человек с «чужим»
   * номером попал сюда.
   *
   * Необязательное: ответы, лежащие в кэше с прошлой версии админки, списка не
   * содержат, и падать на них из-за истории номеров не за что.
   */
  phones?: ClientPhone[];
  /** Ответственный сотрудник: менеджер до продления, продавший — после. */
  responsibleEmployeeId: string | null;
  responsibleEmployeeName: string | null;
  email: string | null;
  birthDate: string | null;
  gender: 'm' | 'f' | null;
  notes: string | null;
  status: ClientStatus;
  clientType: ClientType;
  manualStatusLock: boolean;
  processingBlocked: boolean;
  bitrix24ContactId: string | null;
  preferredMessengerPlatform: MessengerPlatform | null;
  /**
   * Адреса в мессенджерах, чтобы написать ПЕРВЫМИ (ТЗ findchat 2026-07-30).
   *
   * Не путать с `preferredMessengerPlatform` и с диалогами: там канал уже
   * начатой переписки, здесь — куда писать тому, кто нам ещё не писал.
   * Заполняет внешний сервис; руками правятся только waPhone и vkLink.
   */
  messengerContacts: {
    tgUsername: string | null;
    /** Строкой: id телеграма не обязан влезать в число JS. */
    tgUserId: string | null;
    tgLink: string | null;
    maxUserId: string | null;
    waPhone: string | null;
    vkLink: string | null;
    /**
     * Числовой id профиля ВК — ЕДИНСТВЕННЫЙ адрес, по которому в ВК можно
     * написать первым (юзернеймов Wazzup там не принимает). Приходит из
     * диалога сам; самого числа в интерфейсе не показываем — только признак.
     */
    vkUserId?: string | null;
    /** ISO. null — ещё не искали (это НЕ то же, что «искали и не нашли»). */
    lookupAt: string | null;
    /** «НайтиЧат» переслал карточку контакта в чат уведомлений. */
    cardSent?: boolean;
  };
  sourceId: string | null;
  sourceName: string | null;
  registeredAt: string;
  /** ISO-datetime последнего посещения (status='attended') либо null. */
  lastVisitAt: string | null;
  /** Посещаемые группы (П11): куда клиент ходил, с числом визитов. */
  attendedGroups: Array<{ id: string; name: string; visits: number; lastAt: string | null }>;
  branches: Array<{ branchId: string; role: 'primary' | 'secondary' }>;
  activeCard: ClientActiveCard | null;
  relations: ClientRelation[];
  activeSubscriptionsCount: number;
  /** LTV клиента (Σ оплат − возвраты) в копейках — плашка в шапке карточки (B2). */
  ltvKopecks: number;
  /** Дата первой оплаты — от неё считается «Срок жизни»; null, если оплат не было. */
  firstPaidAt: string | null;
  /** Представители с контактами (может быть несколько — мать/отец); [] — нет. */
  wards: Array<{ id: string; name: string; phone: string | null; birthDate: string | null }>;
  representatives: Array<{
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
  }>;
}

export function getClient(id: string): Promise<GetClientResult> {
  return api.get<GetClientResult>(`/clients/${id}`);
}

/** Загрузить фото клиента (П11). FormData — apiFetch не ставит Content-Type сам. */
export function uploadClientPhoto(id: string, file: File): Promise<{ photo_url: string }> {
  const form = new FormData();
  form.append('file', file);
  return api.post<{ photo_url: string }>(`/clients/${id}/photo`, form);
}

// --- Attendance history of a client ----------------------------------------
// Контракт сверен с apps/api attendance.controller (GET /attendance?client_id=)
// + attendance.dto (AttendanceDto). Только нужные UI-поля.

export type AttendanceStatus =
  | 'scheduled'
  | 'attended'
  | 'no_show'
  | 'cancelled'
  | 'no_show_charged';

export interface AttendanceDto {
  id: string;
  class_occurrence_id: string;
  client_id: string;
  status: AttendanceStatus;
  marked_at: string;
  comment: string | null;
}

export function listClientAttendance(clientId: string): Promise<{ data: AttendanceDto[] }> {
  return api.get<{ data: AttendanceDto[] }>(
    `/attendance?client_id=${encodeURIComponent(clientId)}`,
  );
}

export function createClient(body: CreateClientBody): Promise<CreateClientResult> {
  return api.post<CreateClientResult>('/clients', body);
}

export interface UpdateClientBody {
  first_name?: string;
  last_name?: string;
  /**
   * Тип карточки (план 138 §6 п.1). Раньше он задавался только при создании, и
   * совет «переведите карточку в „Клиент и представитель“, если человек сам
   * занимается» был невыполним: гейт записи представителя жёсткий, а тип не
   * правился ничем, кроме пересоздания карточки.
   */
  client_type?: ClientType;
  middle_name?: string | null;
  phone_secondary?: string | null;
  responsible_employee_id?: string | null;
  email?: string | null;
  notes?: string | null;
  gender?: 'm' | 'f' | null;
  birth_date?: string | null;
  source_id?: string | null;
  preferred_messenger_platform?: MessengerPlatform | null;
  /**
   * Адреса мессенджеров, правимые руками. Юзернейм Telegram открыт планом 66:
   * ручная правка помечается, и findchat её больше не перезаписывает. Ссылку
   * t.me сервер пересобирает сам, числовые id платформ не принимает.
   */
  tg_username?: string | null;
  wa_phone?: string | null;
  vk_link?: string | null;
}

export function updateClient(id: string, body: UpdateClientBody): Promise<void> {
  return api.patch<void>(`/clients/${id}`, body);
}

// --- Карты клиента ----------------------------------------------------------
// Выпуск / отзыв пластиковой или виртуальной карты. Сканер QR/штрих-кода вводит
// номер как клавиатура; backend сам валидирует уникальность номера.

export interface IssueCardBody {
  card_type: 'physical' | 'virtual';
  card_number: string;
}

export function issueCard(clientId: string, body: IssueCardBody): Promise<unknown> {
  return api.post<unknown>(`/clients/${clientId}/cards`, body);
}

export function revokeCard(
  clientId: string,
  cardId: string,
  body: { reason?: string },
): Promise<unknown> {
  return api.post<unknown>(`/clients/${clientId}/cards/${cardId}/revoke`, body);
}

export function archiveClient(id: string): Promise<void> {
  return api.post<void>(`/clients/${id}/archive`);
}

export function unarchiveClient(id: string): Promise<void> {
  return api.post<void>(`/clients/${id}/unarchive`);
}

// «Отказался» (refused) — ручная пометка статуса. Мирорит archive/unarchive:
// refuse выставляет status='refused' + manual_status_lock; unrefuse снимает lock
// и ставит плейсхолдер 'lead' с последующим пересчётом (backend).
export function refuseClient(id: string): Promise<void> {
  return api.post<void>(`/clients/${id}/refuse`);
}

export function unrefuseClient(id: string): Promise<void> {
  return api.post<void>(`/clients/${id}/unrefuse`);
}

export function listBranches(): Promise<{ items: BranchListItem[] }> {
  return api.get<{ items: BranchListItem[] }>('/clients/branches');
}

export function getActiveConsentVersion(): Promise<ActiveConsentVersion> {
  return api.get<ActiveConsentVersion>('/clients/consent/active-version');
}

// --- Настройка обязательных полей формы создания клиента --------------------
// Контракт сверен с apps/api clients form-settings endpoint:
// GET/PUT /clients/form-settings → { required_fields }. Ключи — из набора:
// 'email' | 'birth_date' | 'gender' | 'middle_name' | 'source_id'.

/** Ключ конфигурируемого обязательного поля формы создания клиента. */
export type ClientFormFieldKey =
  | 'email'
  | 'birth_date'
  | 'gender'
  | 'middle_name'
  | 'source_id'
  // План 66 Ф6: появились в форме создания вместе с настройкой обязательности.
  | 'phone_secondary'
  | 'responsible_employee_id';

export interface ClientFormSettings {
  required_fields: ClientFormFieldKey[];
}

export function getFormSettings(): Promise<ClientFormSettings> {
  return api.get<ClientFormSettings>('/clients/form-settings');
}

export function putFormSettings(body: ClientFormSettings): Promise<ClientFormSettings> {
  return api.put<ClientFormSettings>('/clients/form-settings', body);
}

// --- Дубли по телефону + объединение ----------------------------------------
export interface DuplicateClient {
  id: string;
  name: string;
  phone: string | null;
  status: ClientStatus;
  registeredAt: string;
  /** По какому номеру совпало — второй номер равноценен основному (2026-07-27). */
  matchedOn?: 'primary' | 'secondary' | null;
  /**
   * Чем совпало. `name` — только фамилия и имя, то есть запросто однофамильцы:
   * такую пару нельзя объединять без подтверждения человека (план 94).
   */
  reason?: 'phone' | 'name';
  /**
   * Основной филиал и город карточки (правка владельца 29.09): тёзки из разных
   * городов без них неразличимы. Подпись собирает `duplicateLocationLabel`.
   */
  primaryBranchName?: string | null;
  cityName?: string | null;
  /** id основного филиала — для выбора «основной филиал после объединения». */
  primaryBranchId?: string | null;
}

export function getClientDuplicates(clientId: string): Promise<{ items: DuplicateClient[] }> {
  return api.get<{ items: DuplicateClient[] }>(`/clients/${clientId}/duplicates`);
}

/** Кластер потенциальных дублей (по телефону или ФИО) — страница «Поиск дубликатов». */
export interface DuplicateCluster {
  reason: 'phone' | 'name';
  key: string;
  clients: DuplicateClient[];
}

/**
 * Поиск контакта для ручного добавления в пару объединения (план 68 П6).
 *
 * Скан находит только тех, у кого совпал телефон или ФИО. Владельцу нужно
 * объединять и карточки с нулём совпадений — например, когда человек записан
 * то как «Кристина», то как «Кристина Ю.» с разными номерами.
 */
export async function searchClientsForMerge(q: string): Promise<DuplicateClient[]> {
  const res = await listClients({ q, limit: 10 });
  return (
    res.items
      // Уже объединённые карточки предлагать нельзя: сервер откажет
      // (b24.merge.already_merged), да и человеку они не нужны.
      .filter((c) => c.mergedIntoClientId === null)
      .map((c) => ({
        id: c.id,
        name: [c.lastName, c.firstName].filter(Boolean).join(' ').trim(),
        phone: c.phone,
        status: c.status,
        primaryBranchName: c.primaryBranchName ?? null,
        cityName: c.cityName ?? null,
        primaryBranchId: c.primaryBranchId ?? null,
        // Списку регистрация не нужна, поэтому её там нет; для выбора победителя
        // «по стажу» карточку добираем отдельно — см. addManual в модалке.
        registeredAt: '',
      }))
  );
}

export function scanClientDuplicates(params?: {
  limit?: number;
  offset?: number;
  /** Фильтр считает сервер: иначе счётчик «показано N из M» врёт. */
  reason?: 'phone' | 'name';
  /** Поиск по имени или телефону участника — тоже на сервере, по той же причине. */
  q?: string;
}): Promise<{ clusters: DuplicateCluster[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  if (params?.reason) qs.set('reason', params.reason);
  if (params?.q) qs.set('q', params.q);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get<{ clusters: DuplicateCluster[]; total: number }>(
    `/clients/duplicates/scan${suffix}`,
  );
}

/**
 * Дубли КОНКРЕТНОЙ карточки. `match: 'all'` добавляет совпадение по ФИО —
 * только для экрана «найти дубли» из списка. Баннер в карточке и проверка
 * при возврате из архива ходят без него: однофамильцев сотни групп, и по
 * умолчанию они закричали бы «дубль» на каждого тёзку.
 */
export function findClientDuplicates(
  clientId: string,
  match: 'phone' | 'all',
): Promise<{ items: DuplicateClient[] }> {
  return api.get<{ items: DuplicateClient[] }>(
    `/clients/${clientId}/duplicates${match === 'all' ? '?match=all' : ''}`,
  );
}

/** Объединить дубль: winnerId остаётся, loserId поглощается. */
/**
 * @param primaryBranchId основной филиал после объединения — выбор в окне
 * объединения (правка владельца 29.09). Сервер примет только филиал одной из
 * двух карточек; не задан — основным остаётся филиал победителя.
 */
export function mergeClients(
  winnerId: string,
  loserId: string,
  reason?: string,
  primaryBranchId?: string,
): Promise<unknown> {
  return api.post<unknown>(`/clients/${winnerId}/merge`, {
    loser_client_id: loserId,
    ...(reason ? { reason } : {}),
    ...(primaryBranchId ? { primary_branch_id: primaryBranchId } : {}),
  });
}

// --- Ручная смена статуса + площадки (правки владельца) ----------------------
/** Сегменты, которые можно выставить вручную. */
export type ManualClientStatus =
  | 'lead'
  | 'newcomer'
  | 'member'
  | 'ending'
  | 'lapsed'
  | 'dormant'
  | 'refused'
  | 'representative';

export function setClientStatus(id: string, status: ManualClientStatus): Promise<unknown> {
  return api.post<unknown>(`/clients/${id}/status`, { status });
}

/** Снять ручной лок → авто-пересчёт сегмента. */
export function clearClientStatus(id: string): Promise<unknown> {
  return api.post<unknown>(`/clients/${id}/status/clear`);
}

export function setClientPrimaryBranch(id: string, branchId: string): Promise<unknown> {
  return api.post<unknown>(`/clients/${id}/primary-branch`, { branch_id: branchId });
}

// --- Смена телефона клиента + заявки от клиента ------------------------------

/** Сотрудник меняет основной номер напрямую (право client:edit_phone). */
export function updateClientPhone(
  clientId: string,
  phone: string,
): Promise<{ clientId: string; oldPhone: string | null; newPhone: string }> {
  return api.patch<{ clientId: string; oldPhone: string | null; newPhone: string }>(
    `/clients/${clientId}/phone`,
    { phone },
  );
}

/**
 * Снять БЫВШИЙ номер с карточки (разбор 17.08).
 *
 * Пока прежний номер висит бывшим, отдать его настоящему владельцу нельзя:
 * занятость считается по всем номерам всех карточек, включая бывшие. Право то
 * же, что у смены основного номера.
 */
export function releaseFormerPhone(
  clientId: string,
  phone: string,
): Promise<{ clientId: string; phone: string }> {
  return api.post<{ clientId: string; phone: string }>(`/clients/${clientId}/phones/release`, {
    phone,
  });
}

/** Заявка клиента на смену номера (создаётся из мини-аппа, решает сотрудник). */
export interface PhoneChangeRequest {
  id: string;
  clientId: string;
  clientName: string;
  target: 'primary' | 'secondary';
  currentPhone: string | null;
  requestedPhone: string;
  createdAt: string;
}

export function listPhoneChangeRequests(): Promise<{ items: PhoneChangeRequest[] }> {
  return api.get<{ items: PhoneChangeRequest[] }>('/clients/phone-change-requests');
}

export function approvePhoneChangeRequest(id: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`/clients/phone-change-requests/${id}/approve`, {});
}

export function rejectPhoneChangeRequest(id: string, reason?: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`/clients/phone-change-requests/${id}/reject`, {
    ...(reason ? { reason } : {}),
  });
}
